/**
 * Feedback Generator
 * Genererer feedback baseret på feedbackRules og match results
 */

import { translateDimension } from '../utils/translations.js'

/**
 * Match feedback baseret på feedbackRules
 * @param {Array} feedbackRules - Rules fra topic
 * @param {Object} dimensionMatches - Match scores pr. dimension
 * @param {Object} lowMatches - Dimensions med lav match
 * @returns {Array} Feedback items til visning
 */
export function generateFeedback(feedbackRules, dimensionMatches, lowMatches) {
  const feedback = []
  
  if (!feedbackRules || feedbackRules.length === 0) {
    return feedback
  }
  
  // Check hver rule
  for (const rule of feedbackRules) {
    const { when, show } = rule
    
    if (!when || !show) {
      continue
    }
    
    // Check dimension match condition
    if (when.dimension) {
      const matchScore = dimensionMatches[when.dimension]
      
      if (matchScore === undefined) {
        continue
      }
      
      // Check match level
      if (when.match === 'low' && matchScore >= 0.6) {
        continue
      }
      
      if (when.match === 'very_low' && matchScore >= 0.4) {
        continue
      }
      
      if (when.match === 'high' && matchScore < 0.8) {
        continue
      }
      
      // Rule matches - add feedback
      feedback.push({
        dimension: when.dimension,
        matchScore,
        ...show,
        priority: show.priority || 'medium'
      })
    }
  }
  
  // Sort by priority
  const priorityOrder = { high: 3, medium: 2, low: 1 }
  feedback.sort((a, b) => {
    return (priorityOrder[b.priority] || 2) - (priorityOrder[a.priority] || 2)
  })
  
  return feedback
}

/**
 * Generer generel feedback baseret på match patterns - Dynamisk og værdifuld
 * @param {Object} dimensionMatches - Match scores pr. dimension
 * @param {Array} lowMatches - Dimensions med lav match
 * @returns {Object} Generel feedback med insigter og action items
 */
export function generateGeneralFeedback(dimensionMatches, lowMatches) {
  const dimensions = Object.keys(dimensionMatches)
  const avgMatch = dimensions.length > 0
    ? Object.values(dimensionMatches).reduce((a, b) => {
        const scoreA = typeof a === 'object' ? a.score : a
        const scoreB = typeof b === 'object' ? b.score : b
        return scoreA + scoreB
      }, 0) / dimensions.length
    : 0
  
  const feedback = {
    overallMatch: avgMatch,
    strengths: [],
    areasForGrowth: [],
    insights: [],
    actionItems: [],
    summary: ''
  }
  
  // Find styrker (high matches)
  const strengths = []
  const areasForGrowth = []
  
  for (const [dimension, match] of Object.entries(dimensionMatches)) {
    const score = typeof match === 'object' ? match.score : match
    const level = typeof match === 'object' ? match.level : (score >= 0.8 ? 'high' : score >= 0.6 ? 'medium' : 'low')
    
    if (score >= 0.8) {
      strengths.push({
        dimension,
        score,
        level,
        readableName: translateDimension(dimension)
      })
    } else if (score < 0.6) {
      areasForGrowth.push({
        dimension,
        score,
        level,
        readableName: translateDimension(dimension)
      })
    }
  }
  
  feedback.strengths = strengths
  feedback.areasForGrowth = areasForGrowth
  
  // Generate dynamic insights based on patterns
  const insights = []
  const actionItems = []
  
  // Insight 1: Overall match level
  if (avgMatch >= 0.85) {
    insights.push({
      type: 'strength',
      title: 'Stærk grundlæggende alignment',
      description: `Med ${(avgMatch * 100).toFixed(0)}% match har I en solid fundament for jeres relation. I ser verden på lignende måder på de fleste områder.`
    })
    actionItems.push({
      priority: 'low',
      text: 'Fortsæt med at kommunikere åbent og genbesøg disse emner regelmæssigt'
    })
  } else if (avgMatch >= 0.7) {
    insights.push({
      type: 'neutral',
      title: 'God grundlæggende alignment',
      description: `Med ${(avgMatch * 100).toFixed(0)}% match har I en god basis, men der er også områder hvor I ser tingene forskelligt. Dette er normalt og kan faktisk være en styrke.`
    })
    actionItems.push({
      priority: 'medium',
      text: 'Fokusér på de områder hvor I er mest forskellige - de kan være kilder til vækst'
    })
  } else if (avgMatch >= 0.5) {
    insights.push({
      type: 'growth',
      title: 'Forskelle der kan udforskes',
      description: `Med ${(avgMatch * 100).toFixed(0)}% match er der betydelige forskelle i jeres præferencer. Dette er ikke nødvendigvis et problem - det kan være en mulighed for at lære hinanden bedre at kende.`
    })
    actionItems.push({
      priority: 'high',
      text: 'Brug forskellene som samtaleemner - spørg hinanden om hvad der ligger bag jeres forskellige svar'
    })
  } else {
    insights.push({
      type: 'attention',
      title: 'Betydelige forskelle identificeret',
      description: `Med ${(avgMatch * 100).toFixed(0)}% match er der store forskelle i jeres præferencer. Dette kan skabe udfordringer hvis ikke I taler om det, men også muligheder for at forstå hinanden dybere.`
    })
    actionItems.push({
      priority: 'high',
      text: 'Prioriter at tale om de områder hvor I er mest forskellige - start med ét område ad gangen'
    })
  }
  
  // Insight 2: Number of strengths
  if (strengths.length >= 3) {
    insights.push({
      type: 'strength',
      title: `${strengths.length} områder hvor I matcher godt`,
      description: `I har stærk alignment på: ${strengths.slice(0, 3).map(s => s.readableName).join(', ')}${strengths.length > 3 ? ' og flere' : ''}. Dette er jeres fundament.`
    })
  }
  
  // Insight 3: Areas for growth
  if (areasForGrowth.length > 0) {
    const topArea = areasForGrowth[0]
    insights.push({
      type: 'growth',
      title: `${areasForGrowth.length} områder til udvikling`,
      description: `På ${topArea.readableName} har I ${((1 - topArea.score) * 100).toFixed(0)}% forskel. Dette er et godt sted at starte en samtale om hvad der er vigtigt for jer hver især.`
    })
    
    actionItems.push({
      priority: 'high',
      text: `Start med at tale om ${topArea.readableName} - spørg hinanden: "Hvad betyder det for dig når...?"`
    })
  }
  
  // Insight 4: Pattern recognition
  if (areasForGrowth.length >= 3) {
    insights.push({
      type: 'pattern',
      title: 'Mønster identificeret',
      description: 'I har flere områder med forskelle. Dette kan pege på forskellige kommunikationsstile eller behov. Overvej at tale om hvordan I kommunikerer bedst sammen.'
    })
    actionItems.push({
      priority: 'medium',
      text: 'Overvej at bruge et kommunikationsmodul for at forbedre jeres dialog'
    })
  }
  
  // Generate dynamic summary based on insights
  if (strengths.length >= areasForGrowth.length && avgMatch >= 0.7) {
    feedback.summary = `I har stærk alignment på ${strengths.length} områder og ${areasForGrowth.length} områder til udvikling. Dette er et sundt mix - I har et solidt fundament og også muligheder for vækst.`
  } else if (areasForGrowth.length > strengths.length) {
    feedback.summary = `I har identificeret ${areasForGrowth.length} områder hvor I ser tingene forskelligt. Dette er ikke et problem - det er information. Brug det til at forstå hinanden bedre gennem åben dialog.`
  } else {
    feedback.summary = `Med ${(avgMatch * 100).toFixed(0)}% match har I en ${avgMatch >= 0.7 ? 'god' : 'varieret'} grundlæggende alignment. ${strengths.length > 0 ? `I matcher godt på ${strengths.length} områder.` : ''} ${areasForGrowth.length > 0 ? `Fokusér på at forstå forskellene på de ${areasForGrowth.length} områder hvor I er mest forskellige.` : ''}`
  }
  
  feedback.insights = insights
  feedback.actionItems = actionItems
  
  return feedback
}
